import { Stack } from "@mui/material";
import { useMemo } from "react";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { oids } from "@/configs/Settings";
import type { ClinicalInsightChildProps } from "src-next/modules/clinical-insights/types/analysisTypes";
import { useProblemListAnalysisData } from "src-next/modules/clinical-insights/hooks/useProblemListAnalysisData";
import { useGetPractitionerName, normalizePatientId, patientDisplayName } from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import { getInsightAnalysisTexts } from "src-next/modules/clinical-insights/components/workspace/config/analysisInsightConfig";
import { useInsightEmptyLabels } from "src-next/modules/clinical-insights/components/workspace/config/insightEmptyLabels";
import { InsightWorkspace } from "src-next/modules/clinical-insights/components/workspace/shell/InsightWorkspace";
import type { InsightChartItem, InsightPatientRow } from "src-next/modules/clinical-insights/types/insightWorkspaceTypes";
import type { ProblemData } from "src-next/modules/clinical-insights/transformers/processProblemEntries";

const texts = getInsightAnalysisTexts("problemas");

const STATUS_TRANSLATION: Record<string, string> = {
  aberta: "Em Aberto",
  melhora: "Melhora",
  piora: "Piora",
  estável: "Estável",
};

export function ProblemListAnalysis({
  dateRange,
  practitionerId = "all",
  practitioner,
  accentColor,
  onPatientStatsChange,
  practitionerFilter: practitionerSelectSlot,
  onLoadPhaseChange,
  onSelectAllPractitioners,
  onPreviousPeriod,
  periodDisplayLabel,
  isActive = true,
}: ClinicalInsightChildProps) {
  const { organizationId } = useOrganizationContext();
  const author = `${oids.cnpj}-${organizationId}`;
  const practitionerFilter = practitionerId || "all";
  const getPractitionerName = useGetPractitionerName(practitioner);

  const { records: problems, patients, loadPhase, patientsProgress, hasData, retry } =
    useProblemListAnalysisData({
      author,
      practitionerFilter,
      dateRange,
      getPractitionerName,
      onLoadPhaseChange,
      fetchEnabled: isActive,
    });

  const filterResetKey = `${practitionerFilter}-${dateRange?.startDate ?? ""}-${dateRange?.endDate ?? ""}`;
  const { emptyPeriodLabel, emptyPractitionerLabel } = useInsightEmptyLabels(
    dateRange,
    practitionerFilter,
    practitioner,
    periodDisplayLabel
  );

  const statusItems: InsightChartItem[] = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of problems) {
      const s = p.status || "aberta";
      map.set(s, (map.get(s) || 0) + 1);
    }
    return Array.from(map.entries())
      .map(([key, value]) => ({
        key,
        label: STATUS_TRANSLATION[key] || key.charAt(0).toUpperCase() + key.slice(1),
        value,
      }))
      .sort((a, b) => b.value - a.value);
  }, [problems]);

  const rankingItems: InsightChartItem[] = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of problems) {
      map.set(p.problem, (map.get(p.problem) || 0) + 1);
    }
    return Array.from(map.entries())
      .map(([label, value]) => ({ key: label, label, value }))
      .sort((a, b) => b.value - a.value);
  }, [problems]);

  const patientRows: InsightPatientRow[] = useMemo(() => {
    const groups = new Map<string, { name: string; items: ProblemData[] }>();
    for (const problem of problems) {
      const patientId = normalizePatientId(problem.patientId);
      if (!groups.has(patientId)) {
        groups.set(patientId, {
          name: patientDisplayName(patients.get(patientId)),
          items: [],
        });
      }
      groups.get(patientId)!.items.push(problem);
    }
    return Array.from(groups.entries()).map(([patientId, group]) => ({
      patientId,
      patientName: group.name,
      patient: patients.get(patientId),
      items: group.items.map((item) => {
        const statusKey = item.status || "aberta";
        return {
          itemKey: item.problem,
          label: item.problem,
          statusKey,
          statusLabel:
            STATUS_TRANSLATION[statusKey] ||
            statusKey.charAt(0).toUpperCase() + statusKey.slice(1),
          practitioner: item.practitioner,
          date: item.date,
        };
      }),
    }));
  }, [problems, patients]);

  const statusInsightTitle = useMemo(() => {
    if (statusItems.length === 0) return "";
    const sorted = [...statusItems].sort((a, b) => b.value - a.value);
    const total = problems.length;
    const percentage = total > 0 ? Math.round((sorted[0].value / total) * 100) : 0;
    return `${percentage}% dos ${texts.itemPlural} estão como '${sorted[0].label}'`;
  }, [statusItems, problems.length]);

  return (
    <Stack width="100%" height="100%" spacing={0} sx={{ minHeight: 0 }}>
      <InsightWorkspace
        accentColor={accentColor ?? "#FF5722"}
        texts={texts}
        practitionerFilter={practitionerSelectSlot}
        filterResetKey={filterResetKey}
        loadPhase={loadPhase}
        patientsProgress={patientsProgress}
        hasData={hasData}
        isEmpty={problems.length === 0}
        emptyPeriodLabel={emptyPeriodLabel}
        emptyPractitionerLabel={emptyPractitionerLabel}
        showClearPractitionerAction={practitionerFilter !== "all"}
        onClearPractitioner={onSelectAllPractitioners}
        onPreviousPeriod={onPreviousPeriod}
        onRetry={retry}
        statusChart={{ insightText: statusInsightTitle, items: statusItems }}
        rankingChart={{ insightText: texts.rankingHint, items: rankingItems }}
        patientRows={patientRows}
        statusLabelByKey={STATUS_TRANSLATION}
        onPatientStatsChange={onPatientStatsChange}
      />
    </Stack>
  );
}
